import { useCallback, useMemo, useState, useEffect } from "react";
import useGetReportAdditionalCost from "hooks/organization/reports/types/useGetReportAdditionalCost";
import { capitalCase } from "change-case";
import JobModal from "components/JobModal/JobModal";
import usePaginatedResources from "hooks/usePaginatedResources";

export default function useReportTableAdditionalCost(startDate, endDate) {
  const [openModal, setOpenModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const [getReportAdditionalCost, { data, loading, error }] = useGetReportAdditionalCost(false);

  const handleOpenModal = useCallback((jobId: string) => {
    setSelectedJobId(jobId);
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedJobId(null);
    setOpenModal(false);
  }, []);

  const paginate = useCallback(
    ({ page = 1, first = 25 } = {}) => {
      const variables = {
        startDate,
        endDate,
        page,
        first,
      };
      getReportAdditionalCost({ variables });
    },
    [startDate, endDate, getReportAdditionalCost]
  );

  const pagination = usePaginatedResources({
    paginate,
    paginatorInfo: data?.getReportAdditionalCost?.paginatorInfo,
  });

  const columns = useMemo(() => {
    return [
      {
        Header: "Job ID",
        accessor: "job_id",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Additional Cost Id",
        accessor: "additional_cost_id",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Additional Cost Name",
        accessor: "additional_cost_name",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Additional Cost Quantity",
        accessor: "additional_cost_quantity",
        Cell: ({ value }) => value ?? "",
        filter: "includes",
        disableSortBy: true,
        align: "right",
      },
      {
        Header: "Additional Cost Amount",
        accessor: "additional_cost_amount",
        Cell: ({ value }) => (value / 100).toFixed(2) ?? "",
        disableSortBy: true,
        align: "right",
      },
    ];
  }, [handleOpenModal]);

  const tableData = useMemo(() => {
    return {
      columns,
      rows: data?.getReportAdditionalCost?.data || [],
      modal: <JobModal jobId={selectedJobId} open={openModal} onClose={handleCloseModal} />,
    };
  }, [columns, data, selectedJobId, openModal, handleCloseModal]);

  return {
    columns,
    data,
    loading,
    error,
    tableData,
    pagination,
    getReportAdditionalCost: paginate,
  } as const;
}
